import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Github, Linkedin, Twitter } from 'lucide-react';

const SOCIALS = [
  { href: 'https://github.com/knowlez', label: 'GitHub', Icon: Github },
  { href: 'https://www.linkedin.com/company/knowlez', label: 'LinkedIn', Icon: Linkedin },
  { href: 'https://x.com/knowlez', label: 'X', Icon: Twitter },
];

export function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-ink-100 mt-24 bg-paper-50">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8 text-[13px] text-ink-600">
        <div className="col-span-2 md:col-span-1 space-y-3">
          <div className="flex items-center gap-2.5 text-ink-900 font-semibold">
            <img src="/logo.png" alt={t('brand.name')} className="h-7 w-7 rounded-md object-contain" />
            <span className="display text-[16px]">{t('brand.name')}</span>
          </div>
          <p className="text-ink-500 leading-relaxed max-w-xs">
            {t('brand.tagline', {
              defaultValue: 'Every document. Every signal. Every thought. One knowing plane.',
            })}
          </p>
        </div>

        <div className="space-y-2.5">
          <h4 className="text-[11px] uppercase tracking-[0.16em] text-ink-400 font-semibold">
            {t('footer.product', { defaultValue: 'Product' })}
          </h4>
          <Link to="/pricing" className="block hover:text-ink-900 transition">
            {t('nav.pricing', { defaultValue: 'Pricing' })}
          </Link>
          <Link to="/compare" className="block hover:text-ink-900 transition">
            {t('nav.compare', { defaultValue: 'Compare' })}
          </Link>
          <Link to="/demo" className="block hover:text-ink-900 transition">
            {t('nav.demo', { defaultValue: 'Demo' })}
          </Link>
        </div>

        <div className="space-y-2.5">
          <h4 className="text-[11px] uppercase tracking-[0.16em] text-ink-400 font-semibold">
            {t('footer.legal_section', { defaultValue: 'Legal' })}
          </h4>
          <Link to="/legal/terms" className="block hover:text-ink-900 transition">
            {t('footer.terms', { defaultValue: 'Terms' })}
          </Link>
          <Link to="/legal/privacy" className="block hover:text-ink-900 transition">
            {t('footer.privacy', { defaultValue: 'Privacy' })}
          </Link>
          <Link to="/legal/dpa" className="block hover:text-ink-900 transition">
            {t('footer.dpa', { defaultValue: 'DPA' })}
          </Link>
          <Link to="/security" className="block hover:text-ink-900 transition">
            {t('footer.security', { defaultValue: 'Security' })}
          </Link>
        </div>

        <div className="space-y-2.5">
          <h4 className="text-[11px] uppercase tracking-[0.16em] text-ink-400 font-semibold">
            {t('footer.platform', { defaultValue: 'Platform' })}
          </h4>
          <Link to="/status" className="block hover:text-ink-900 transition">
            {t('footer.status', { defaultValue: 'Status' })}
          </Link>
          <Link to="/changelog" className="block hover:text-ink-900 transition">
            {t('footer.changelog', { defaultValue: 'Changelog' })}
          </Link>
          <Link to="/oss" className="block hover:text-ink-900 transition">
            {t('footer.oss', { defaultValue: 'Open source' })}
          </Link>
          <Link to="/support" className="block hover:text-ink-900 transition">
            {t('footer.support', { defaultValue: 'Support' })}
          </Link>
          <a href="mailto:support@knowlez.com" className="block hover:text-ink-900 transition">
            {t('footer.contact', { defaultValue: 'Contact' })}
          </a>
        </div>

        <div className="space-y-2.5">
          <h4 className="text-[11px] uppercase tracking-[0.16em] text-ink-400 font-semibold">
            {t('footer.account', { defaultValue: 'Account' })}
          </h4>
          <Link to="/login" className="block hover:text-ink-900 transition">
            {t('nav.login', { defaultValue: 'Log in' })}
          </Link>
          <Link to="/signup" className="block hover:text-ink-900 transition">
            {t('nav.signup', { defaultValue: 'Sign up' })}
          </Link>
        </div>
      </div>

      <div className="border-t border-ink-100">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] text-ink-400">
          <span>{t('footer.rights', { defaultValue: '© 2026 Knowlez. All rights reserved.' })}</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-ink-400 hover:text-ink-900 transition"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <span className="font-mono">knowlez.com</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
